import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';

type ToastSeverity = 'success' | 'info' | 'warn' | 'error';

export interface ToastOptions {
  severity?: ToastSeverity;
  summary?: string;
  life?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  constructor(private messageService: MessageService) {}

  show(message: string, options: ToastOptions = {}): void {
    const severity = options.severity || this.inferSeverity(message);

    this.messageService.add({
      severity,
      summary: options.summary || this.getSummary(severity),
      detail: message,
      life: options.life ?? 3000
    });
  }

  success(message: string, life = 3000): void {
    this.show(message, { severity: 'success', life });
  }

  error(message: string, life = 3000): void {
    this.show(message, { severity: 'error', life });
  }

  warn(message: string, life = 3000): void {
    this.show(message, { severity: 'warn', life });
  }

  info(message: string, life = 3000): void {
    this.show(message, { severity: 'info', life });
  }

  open(message: string, _action?: string, config?: { duration?: number }): void {
    this.show(message, { life: config?.duration ?? 3000 });
  }

  private inferSeverity(message: string): ToastSeverity {
    const text = message.toLowerCase();

    if (
      text.includes('success') ||
      text.includes('created successfully') ||
      text.includes('updated successfully') ||
      text.includes('uploaded successfully') ||
      text.includes('downloaded successfully')
    ) {
      return 'success';
    }

    if (
      text.includes('fail') ||
      text.includes('error') ||
      text.includes('invalid') ||
      text.includes('not found') ||
      text.includes('please try again') ||
      text.includes('please select') ||
      text.includes('please enter') ||
      text.includes('please fill')
    ) {
      return 'error';
    }

    if (text.includes('already on')) {
      return 'warn';
    }

    return 'info';
  }

  private getSummary(severity: ToastSeverity): string {
    switch (severity) {
      case 'success':
        return 'Success';
      case 'error':
        return 'Error';
      case 'warn':
        return 'Warning';
      default:
        return 'Info';
    }
  }
}
